/** Stable list field IDs shared by personal views and workspace/board defaults. */
export const LIST_FIELDS = [
  ['title', 'Name'],
  ['key', 'Key'],
  ['status', 'Status'],
  ['priority', 'Priority'],
  ['assignee', 'Assignee'],
  ['points', 'Story points'],
  ['due', 'Due date'],
  ['milestone', 'Milestone'],
  ['sprint', 'Sprint'],
  ['start', 'Start date'],
] as const
export type ListField = (typeof LIST_FIELDS)[number][0]
export const DEFAULT_LIST_FIELDS: ListField[] = [
  'title',
  'key',
  'status',
  'priority',
  'assignee',
  'points',
  'due',
]
export function normalizeListFields(value: unknown): ListField[] {
  if (!Array.isArray(value)) return [...DEFAULT_LIST_FIELDS]
  return [
    'title',
    ...new Set(
      value.filter((id): id is ListField => id !== 'title' && LIST_FIELDS.some(([known]) => known === id)),
    ),
  ]
}
export function resolveListFields(personal: unknown, board: unknown, workspace: unknown) {
  const source =
    personal != null ? 'personal' : board != null ? 'board' : workspace != null ? 'workspace' : 'application'
  return { fields: normalizeListFields(personal ?? board ?? workspace), source }
}
/**
 * Numeric suffixes, prefix first; malformed/missing keys sort after valid keys.
 * Returning zero for otherwise identical keys preserves their input order under
 * ECMAScript's stable Array#sort in both directions.
 */
export function compareTaskKeys(
  a: { key?: string | null; sequence?: number | null },
  b: { key?: string | null; sequence?: number | null },
) {
  const ordinal = (left: string, right: string) => (left < right ? -1 : left > right ? 1 : 0)
  const parts = (task: typeof a) => {
    const match = /^(.*)-(\d+)$/.exec(task.key ?? '')
    const sequence = task.sequence
    return {
      prefix: match?.[1] ?? '',
      number:
        typeof sequence === 'number' && Number.isSafeInteger(sequence) && sequence > 0
          ? BigInt(sequence)
          : match
            ? BigInt(match[2]!)
            : null,
    }
  }
  const left = parts(a)
  const right = parts(b)
  if (left.number === null || right.number === null) {
    if (left.number !== right.number) return left.number === null ? 1 : -1
  } else {
    const prefix = ordinal(left.prefix, right.prefix)
    if (prefix) return prefix
    if (left.number !== right.number) return left.number < right.number ? -1 : 1
  }
  return ordinal(a.key ?? '', b.key ?? '')
}
